"use client";

import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/** A line through data points, drawn in plot (data) coordinates. */
export interface PlotCurve {
  points: readonly (readonly [number, number])[];
  color?: string;
  dashed?: boolean;
}

/** A labelled dot in data coordinates. */
export interface PlotDot {
  x: number;
  y: number;
  label?: string;
  color?: string;
}

const WIDTH = 640;
const HEIGHT = 320;
const MARGIN = { left: 64, right: 24, top: 20, bottom: 56 };
const FONT = 12;
const LABEL_GAP = 8;

function pathFor(
  points: PlotCurve["points"],
  sx: (v: number) => number,
  sy: (v: number) => number
): string {
  return points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${px(sx(x))},${px(sy(y))}`)
    .join(" ");
}

/**
 * A small line graph for physics results — an I–V line, an RC charging curve, a lens
 * image position. The widget supplies data coordinates and formatters, so the same
 * component writes 1,5 or 1.5 as the reader's language does.
 */
export function PhysicsPlot({
  id,
  label,
  description,
  xRange,
  yRange,
  curves,
  dots = [],
  xTitle,
  yTitle,
  formatX,
  formatY,
}: {
  id: string;
  label: string;
  description: string;
  xRange: readonly [number, number];
  yRange: readonly [number, number];
  curves: readonly PlotCurve[];
  dots?: readonly PlotDot[];
  xTitle: string;
  yTitle: string;
  formatX: (value: number) => string;
  formatY: (value: number) => string;
}) {
  const plot = {
    x: MARGIN.left,
    y: MARGIN.top,
    width: WIDTH - MARGIN.left - MARGIN.right,
    height: HEIGHT - MARGIN.top - MARGIN.bottom,
  };
  const xTicks = niceTicks(xRange[0], xRange[1], { count: 5, exact: false });
  const yTicks = niceTicks(yRange[0], yRange[1], { count: 5, exact: false });
  const sx = linearScale(
    [xTicks.min, xTicks.max],
    [plot.x, plot.x + plot.width]
  );
  const sy = linearScale(
    [yTicks.min, yTicks.max],
    [plot.y + plot.height, plot.y]
  );
  return (
    <ChartSvg
      description={description}
      height={HEIGHT}
      id={id}
      label={label}
      width={WIDTH}
    >
      <ValueAxis
        format={formatX}
        orient="bottom"
        plot={plot}
        scale={sx}
        ticks={xTicks.ticks}
      />
      <ValueAxis
        format={formatY}
        orient="left"
        plot={plot}
        scale={sy}
        ticks={yTicks.ticks}
      />
      {curves.map((curve, index) => (
        <path
          d={pathFor(curve.points, sx, sy)}
          fill="none"
          // Curves are positional: the widget builds them in a fixed order.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional series
          key={index}
          stroke={curve.color ?? CHART_COLORS[index % CHART_COLORS.length]}
          strokeDasharray={curve.dashed ? "6 4" : undefined}
          strokeWidth={2.5}
        />
      ))}
      {dots.map((dot) => (
        <g key={`${dot.x}-${dot.y}-${dot.label ?? ""}`}>
          <circle
            cx={px(sx(dot.x))}
            cy={px(sy(dot.y))}
            fill={dot.color ?? CHART_COLORS[3]}
            r={5}
            stroke={CHART_INK.background}
            strokeWidth={1.5}
          />
          {dot.label ? (
            <text
              fill={CHART_INK.text}
              fontSize={FONT}
              textAnchor={
                sx(dot.x) > plot.x + plot.width * 0.7 ? "end" : "start"
              }
              x={px(
                sx(dot.x) +
                  (sx(dot.x) > plot.x + plot.width * 0.7
                    ? -LABEL_GAP
                    : LABEL_GAP)
              )}
              y={px(sy(dot.y) - LABEL_GAP)}
            >
              {dot.label}
            </text>
          ) : null}
        </g>
      ))}
      <text
        fill={CHART_INK.muted}
        fontSize={FONT}
        textAnchor="middle"
        x={px(plot.x + plot.width / 2)}
        y={HEIGHT - 12}
      >
        {xTitle}
      </text>
      <text
        fill={CHART_INK.muted}
        fontSize={FONT}
        textAnchor="middle"
        transform={`rotate(-90 14 ${px(plot.y + plot.height / 2)})`}
        x={14}
        y={px(plot.y + plot.height / 2)}
      >
        {yTitle}
      </text>
    </ChartSvg>
  );
}
